import React, { useMemo } from 'react';
import { Grid2 as Grid } from '@mui/material';
import { ShowSection, IconSectionRow } from 'framework/ui/layout';
import { useShowContext } from 'framework/show';
import { Box } from '@mui/material';
import { TextField } from 'framework/ui/field';
import EmptyTeamSection from '../shared/EmptyTeamSection';
import { EmployeePersonTile } from 'domain/shared/core/PersonTile';
import { EMPTY_VALUE_INDICATOR, formatDecimal } from 'framework/utils';
import { AppIconLabel } from 'framework/ui/mui-builder';
import {
  TeamShowType,
  PROJECT_TEAM_LOCATION_LABELS,
  PROJECT_TEAM_TYPE_LABELS,
  getProjectManager,
  useProjectDataContext,
} from 'domain/shared/newProject';

const GeneralProjectTeamInfo = () => {
  const { data } = useShowContext<TeamShowType>();
  const { allFte, assignedFte, isNeedCreateTeam, team } = data.getData();
  const { data: project } = useProjectDataContext();
  const projectManager = getProjectManager(project);

  const { percentage, difference, isAvailable } = useMemo(() => {
    if (allFte == null) {
      return {
        percentage: null,
        difference: null,
        isAvailable: false,
      };
    }

    const safeAdded = assignedFte ?? 0;

    return {
      percentage: allFte > 0 ? Math.round((assignedFte / allFte) * 100) : assignedFte > 0 ? 100 : 0,
      difference: allFte - safeAdded,
      isAvailable: true,
    };
  }, [allFte, assignedFte]);

  return (
    <ShowSection sx={{ position: 'relative' }}>
      {isNeedCreateTeam && <EmptyTeamSection />}
      <Grid container spacing={2} sx={{ pb: 2 }}>
        <Grid size={6}>
          <IconSectionRow colXl={5} colMd={5}>
            <TextField
              value={
                isAvailable
                  ? `${formatDecimal(assignedFte ?? 0)}/${formatDecimal(allFte)} FTE (${formatDecimal(percentage!!)}%)`
                  : EMPTY_VALUE_INDICATOR
              }
              label={<AppIconLabel sxIcon={{ width: '32px' }} icon="group" tooltipTitle="Assigned/Required FTE" />}
            />

            <TextField
              value={isAvailable ? `${formatDecimal(difference ?? 0)} FTE` : EMPTY_VALUE_INDICATOR}
              label={<AppIconLabel icon="peoplePlus" tooltipTitle="Difference FTE" />}
            />
          </IconSectionRow>

          <IconSectionRow
            colXl={5}
            colMd={5}
            containerProps={{ sx: { height: '50px', alignItems: 'center' } }}
            sxItem={{
              mb: '10px !important',
            }}
          >
            <TextField
              value={team?.location ? PROJECT_TEAM_LOCATION_LABELS[team.location] : EMPTY_VALUE_INDICATOR}
              label={<AppIconLabel icon="location" tooltipTitle="Team location" />}
            />
            <TextField
              value={team?.type ? PROJECT_TEAM_TYPE_LABELS[team.type] : EMPTY_VALUE_INDICATOR}
              label={<AppIconLabel icon="cubes" tooltipTitle="Type" />}
            />
          </IconSectionRow>
        </Grid>

        <Grid size={4}>
          <Box display="flex" justifyContent="space-between">
            <EmployeePersonTile
              sxName={{ fontSize: '14px' }}
              sxPosition={{ fontSize: '12px' }}
              employeeId={projectManager}
              title="Project manager"
            />
            <EmployeePersonTile
              sxName={{ fontSize: '14px' }}
              sxPosition={{ fontSize: '12px' }}
              employeeId={project.accountData?.ownerAlias}
              title="Account manager"
            />
          </Box>
        </Grid>
      </Grid>
    </ShowSection>
  );
};

export default GeneralProjectTeamInfo;
